/**
 * Full-screen modal to submit all challenge picks at once (and accept if PENDING).
 * Each match shows a GameInlineCard-style header + InlineMarketPicker chip strip.
 */

import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import {
  View,
  Text,
  Pressable,
  Platform,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../src/shared/constants/animations';
import { SafeAreaView } from 'react-native-safe-area-context';
import { X } from 'lucide-react-native';
import { BaseModal } from '../ui/BaseModal';
import {
  getRequiredMarketsPerMatch,
  buildMarketsForMatch,
  get1x2OddsFromMarkets,
  getSnapshotMarketsForMatch,
} from '../../src/challenges/helpers/acceptChallengePicks.helper';
import { submitChallengePicks } from '../../src/challenges/helpers/challengeCardApi';
import type { SubmitPickPayload } from '../../src/challenges/helpers/challengeCardApi';
import { useApiClient } from '../../helpers/apiClient.helper';
import { formatDateShort, formatTimeOnly } from '../../helpers/formatDateTime.helper';
import { InlineMarketPicker } from '../create/CreateGamesStep/InlineMarketPicker';
import { isInSameExclusiveFamily } from '../../src/create/constants/exclusiveMarkets.constants';
import { SCORE_PICKER_MARKET_IDS } from '../../src/create/constants/matchDetails.constants';
import type { ChallengeWithStats } from '../../types/challenges';
import type { MatchApiResponse } from '../../src/friends/types/friends.types';
import type { MarketOdds } from '../../src/create/types/create.types';
import { MATCH_WINNER_MARKET_ID } from '../../src/challenges/constants/challenges.constants';
import { getAcceptChallengePicksModalStyles } from './AcceptChallengePicksModal.styles';
import { useAnalytics } from '../../src/analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../src/analytics';
import { getTeamKitConfig } from '../../helpers/teamKit.helper';
import { SoccerKit } from '../ui/SoccerKit';
import { SportIcon } from '../../src/sports/components/SportIcon/SportIcon';
import { TeamCrest } from '../competitions/TeamCrest/TeamCrest';
import { getSportDefinitionOrDefault } from '../../src/sports/sports.constants';
import { useEconomyStore } from '../../src/shared/stores/economyStore';
import type { SnackbarVariant } from '../../src/shared/stores/snackbarStore';

export interface AcceptChallengePicksModalProps {
  visible: boolean;
  onClose: () => void;
  challenge: ChallengeWithStats;
  currentUserId: string;
  /** Pre-fetched matches from the batched context query (avoids per-modal fetch). */
  matches: MatchApiResponse[];
  /** First game ISO date; when in the past, accept is disabled. */
  firstGameDate?: string | null;
  insufficientCoins?: boolean;
  /**
   * Open-board take-on: the viewer is not a participant yet, so the slot must be CLAIMED before
   * picks can be saved (`submit-picks` 404s on a non-participant). Claiming is first-taker-wins
   * and deducts the stake, so it happens on Confirm — never on open — and a loser gets a 409
   * without having paid. Off for friend/group accepts, where the row already exists.
   */
  takeOnOpen?: boolean;
  /** Called with message and variant so parent can show toast/snackbar. */
  onShowMessage?: (payload: { message: string; variant: SnackbarVariant }) => void;
  /** Called after successful submit so parent can refetch. */
  onSuccess?: () => void;
}

interface DraftPick {
  matchId: string;
  marketId: number;
  marketName: string;
  value: string;
  odd: number;
}

function get1x2FromMatch(match: { odds?: { home?: number; draw?: number; away?: number } }): {
  home: number;
  draw: number;
  away: number;
} {
  const o = match.odds ?? {};
  return { home: o.home ?? 2, draw: o.draw ?? 3, away: o.away ?? 2 };
}

const styles = getAcceptChallengePicksModalStyles();

export const AcceptChallengePicksModal: React.FC<AcceptChallengePicksModalProps> = ({
  visible,
  onClose,
  challenge,
  currentUserId,
  matches,
  firstGameDate,
  insufficientCoins = false,
  takeOnOpen = false,
  onShowMessage,
  onSuccess,
}) => {
  const { track } = useAnalytics();
  const isFirstGameStarted = useMemo(
    () =>
      firstGameDate != null && firstGameDate !== '' && new Date(firstGameDate).getTime() <= Date.now(),
    [firstGameDate]
  );
  const apiClient = useApiClient();
  const apiClientRef = useRef(apiClient);
  apiClientRef.current = apiClient;
  const requiredByMatch = useMemo(() => getRequiredMarketsPerMatch(challenge), [challenge]);

  const existingPicks = useMemo(() => {
    const picks = challenge.picks ?? [];
    return picks.filter((p) => p.userId === currentUserId && p.matchId && p.marketId != null);
  }, [challenge.picks, currentUserId]);

  const [draft, setDraft] = useState<DraftPick[]>([]);
  const [submitting, setSubmitting] = useState(false);
  // Only a pending (not-yet-paid) participant can be blocked by coins. The parent computes
  // `insufficientCoins` with that gate. Editing already-submitted picks (creator or an
  // already-accepted participant) never re-charges the stake server-side, so we must not
  // re-block here based on the raw balance — that wrongly locks out anyone who staked all
  // their coins on create/accept.
  const blockedByCoins = insufficientCoins;

  useEffect(() => {
    if (!visible) return;
    const initial: DraftPick[] = existingPicks.map((p) => ({
      matchId: p.matchId,
      marketId: p.marketId,
      marketName: p.marketName ?? '',
      value: p.value ?? '',
      odd: typeof p.odd === 'number' ? p.odd : 1,
    }));
    setDraft(initial);
  }, [visible, existingPicks]);

  const setDraftPick = useCallback(
    (matchId: string, marketId: number, marketName: string, value: string, odd: number) => {
      setDraft((prev) => {
        const rest = prev.filter((p) => {
          if (p.matchId !== matchId) return true;
          if (p.marketId == null) return true;
          if (isInSameExclusiveFamily(p.marketId, marketId)) return false;
          if (p.marketId === marketId) return false;
          return true;
        });
        return [...rest, { matchId, marketId, marketName, value, odd }];
      });
    },
    []
  );

  const getDraftPick = useCallback(
    (matchId: string, marketId: number): DraftPick | undefined =>
      draft.find((p) => p.matchId === matchId && p.marketId === marketId),
    [draft]
  );

  const matchIdsWithRequired = useMemo(
    () => Object.keys(requiredByMatch).filter((id) => (requiredByMatch[id]?.length ?? 0) > 0),
    [requiredByMatch]
  );

  const matchesInOrder = useMemo(() => {
    const idSet = new Set(matchIdsWithRequired);
    return matches.filter((m) => idSet.has(m.id));
  }, [matches, matchIdsWithRequired]);

  useEffect(() => {
    if (!visible || matchesInOrder.length === 0) return;
    setDraft((prev) => {
      type MatchWithOdds = (typeof matchesInOrder)[0];
      const apiMatch = (m: MatchWithOdds) =>
        (m as { odds?: { markets?: Array<{ id: number; name: string; values?: Array<{ value: string; odd: number }> }> } }).odds?.markets ?? [];
      let next = prev;
      for (const match of matchesInOrder) {
        const required = requiredByMatch[match.id] ?? [];
        const snapshotMarkets = getSnapshotMarketsForMatch(challenge.oddsSnapshot, match.id);
        const markets = snapshotMarkets.length > 0 ? snapshotMarkets : apiMatch(match);
        for (const rm of required) {
          if (!SCORE_PICKER_MARKET_IDS.includes(rm.marketId)) continue;
          if (prev.some((p) => p.matchId === match.id && p.marketId === rm.marketId)) continue;
          const apiMarket = markets.find((m) => m.id === rm.marketId);
          const firstVal = apiMarket?.values?.[0];
          if (firstVal) {
            next = next.filter((p) => !(p.matchId === match.id && p.marketId === rm.marketId));
            next = [
              ...next,
              {
                matchId: match.id,
                marketId: rm.marketId,
                marketName: rm.marketName,
                value: String(firstVal.value ?? '').trim() || '0-0',
                odd: Number(firstVal.odd) || 1,
              },
            ];
          }
        }
      }
      return next;
    });
  }, [visible, matchesInOrder, requiredByMatch, challenge.oddsSnapshot]);

  /**
   * The gate is every match in `challenge.matchIds`, NOT just the ones we derived markets for.
   * The server rejects a submission that misses any matchId, and on the open take-on path the
   * stake is already committed by then — so a short `requiredByMatch` must leave the button
   * disabled (a dead button) rather than let someone pay for a submission that cannot land.
   */
  const allRequiredFilled = useMemo(() => {
    const everyMatchId = (challenge.matchIds ?? []).filter(Boolean);
    if (everyMatchId.length === 0) return false;
    for (const matchId of everyMatchId) {
      const required = requiredByMatch[matchId] ?? [];
      // No market derived for this match => nothing the user can fill => never submittable.
      if (required.length === 0) return false;
      for (const rm of required) {
        const cur = getDraftPick(matchId, rm.marketId);
        if (!cur?.value) return false;
      }
    }
    return true;
  }, [challenge.matchIds, requiredByMatch, getDraftPick]);

  const handleSubmit = useCallback(async () => {
    if (isFirstGameStarted || blockedByCoins || !allRequiredFilled || submitting) return;
    setSubmitting(true);
    try {
      const picks: SubmitPickPayload[] = draft
        .filter((p) => p.value)
        .map((p) => ({
          matchId: p.matchId,
          marketId: p.marketId,
          marketName: p.marketName,
          value: p.value,
          odd: p.odd,
        }));
      const stake = challenge.stakeCoins ?? 0;

      // One request, take-on included. `submit-picks` claims the open slot in the same transaction
      // that writes these picks, so there is no window where the stake is gone and the picks are
      // not saved. The previous two-step (claim, then submit) could strand a taker with an ACTIVE
      // staked challenge and no picks, which settles as a total loss.
      const result = await submitChallengePicks(challenge.id, picks, apiClientRef.current);
      if (result.success) {
        // Accepting is the moment worth confirming out loud — the user has just committed a stake
        // and expects to be told it landed. Editing picks on a challenge they were already in is
        // the quieter case.
        const accepted = result.data?.wasPending || result.data?.tookOnOpen;
        const msg = accepted ? 'Challenge accepted. Good luck!' : 'Picks saved.';
        // Both accept shapes stake exactly once server-side, so mirror it locally exactly once.
        if (accepted && stake > 0) {
          useEconomyStore.getState().syncCoins(useEconomyStore.getState().coins - stake);
        }
        track(ANALYTICS_EVENTS.BET_PLACED, {
          challenge_id: challenge.id,
          picks_count: picks.length,
        });
        onShowMessage?.({ message: msg, variant: 'success' });
        onSuccess?.();
        onClose();
      } else {
        // Nothing was charged — the whole thing rolled back — so the modal stays open with the
        // draft intact and the user can simply press Confirm again.
        onShowMessage?.({
          message: result.error
            ?? (takeOnOpen
              ? 'Could not take on this challenge. Someone may have beaten you to it.'
              : 'Failed to submit picks. Please try again.'),
          variant: 'error',
        });
      }
    } finally {
      setSubmitting(false);
    }
  }, [challenge.id, challenge.stakeCoins, draft, allRequiredFilled, submitting, isFirstGameStarted, blockedByCoins, takeOnOpen, onShowMessage, onSuccess, onClose]);

  const isDisabled = isFirstGameStarted || blockedByCoins || !allRequiredFilled || submitting;

  return (
    <BaseModal
      isVisible={visible}
      onDismiss={onClose}
      blurIntensity={20}
      contentAlignment="flex-end"
      contentContainerStyle={styles.sheetContainer}
      testID="accept-challenge-picks-modal"
    >
      <SafeAreaView style={styles.sheetContent} edges={['top', 'bottom']}>
        <View style={styles.header}>
          <Text style={styles.title}>Make Your Picks</Text>
          <Pressable
            onPress={onClose}
            style={({ pressed, hovered }) => [
              styles.closeButton,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && styles.closeButtonPressed,
              hovered && Platform.OS === 'web' && styles.closeButtonHover,
            ]}
            accessibilityRole="button"
            accessibilityLabel="Close"
          >
            <X size={24} color="#9CA3AF" />
          </Pressable>
        </View>

        {matches.length === 0 ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#A78BFA" />
          </View>
        ) : (
          <ScrollView
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="handled"
          >
            {matchesInOrder.map((match) => {
              const required = requiredByMatch[match.id] ?? [];
              const has1x2 = required.some((r) => r.marketId === MATCH_WINNER_MARKET_ID);
              const otherMarkets = required.filter((r) => r.marketId !== MATCH_WINNER_MARKET_ID);
              // Markets the challenge already offers are unlockable for non-Pro invitees — the server
              // accepts these picks (they live in the challenge's oddsSnapshot). New premium markets
              // outside this set stay locked, matching the submit-picks gate.
              const unlockedPremiumMarketIds = new Set(required.map((r) => r.marketId));

              const homeName =
                typeof match.homeTeam === 'string'
                  ? match.homeTeam
                  : (match.homeTeam as { name?: string })?.name ?? 'Home';
              const awayName =
                typeof match.awayTeam === 'string'
                  ? match.awayTeam
                  : (match.awayTeam as { name?: string })?.name ?? 'Away';

              const homeTeamForKit =
                typeof match.homeTeam === 'string'
                  ? match.homeTeam
                  : { name: match.homeTeam?.name ?? homeName, kitPrimary: match.homeTeam?.kitPrimary, kitSecondary: match.homeTeam?.kitSecondary, kitType: match.homeTeam?.kitType };
              const awayTeamForKit =
                typeof match.awayTeam === 'string'
                  ? match.awayTeam
                  : { name: match.awayTeam?.name ?? awayName, kitPrimary: match.awayTeam?.kitPrimary, kitSecondary: match.awayTeam?.kitSecondary, kitType: match.awayTeam?.kitType };
              const homeKit = getTeamKitConfig(homeTeamForKit);
              const awayKit = getTeamKitConfig(awayTeamForKit);

              // Which sport this bet belongs to (specs/multi-sport-challenges). An invitee sees a
              // mixed list of matches, so each one has to say what it is; a kit graphic beside a
              // basketball game would actively mislead.
              const matchSport = getSportDefinitionOrDefault(
                (match as { sport?: string | null }).sport,
              );
              const matchUsesKits = matchSport.identityMode === 'KIT';
              // A FIELD fixture has no two sides (specs/formula-one-challenges). Rendering it
              // through the pairing above would read "Home vs Away", because the server sends null
              // teams for a race — so it is named by its event and circuit instead.
              const matchIsField = matchSport.shape === 'FIELD';
              const fieldEventName =
                (match as { round?: string | null }).round ?? matchSport.label;
              const fieldVenue = (match as { venue?: string | null }).venue ?? '';

              const dateSource = match.date ?? '';
              const headerDate = dateSource ? formatDateShort(dateSource) : '';
              const headerTime = dateSource ? formatTimeOnly(dateSource) : '';

              const snapshotMarkets = getSnapshotMarketsForMatch(challenge.oddsSnapshot, match.id);
              const oddsWithMarkets = match.odds as unknown as
                | { markets?: Array<{ id: number; name: string; values?: Array<{ value: string; odd: number }> }> }
                | undefined;
              const liveMarkets = Array.isArray(oddsWithMarkets?.markets) ? oddsWithMarkets.markets : [];
              const frozenOrLiveMarkets = snapshotMarkets.length > 0 ? snapshotMarkets : liveMarkets;
              const odds1x2 = get1x2OddsFromMarkets(frozenOrLiveMarkets) ?? get1x2FromMatch(match);

              // The draw follows the PRIMARY MARKET, not the sport's final-score rule
              // (specs/basketball-regulation-1x2). Basketball's regulation 1X2 has a real draw;
              // its moneyline does not. `get1x2FromMatch` defaults `draw` to 3.00 when no draw
              // price exists, so building this unconditionally once showed the invitee
              // "Home / Draw 3.00 / Away" on a two-way market.
              const matchWinnerMarket: MarketOdds | null = has1x2
                ? {
                    id: MATCH_WINNER_MARKET_ID,
                    name: matchSport.primaryMarketName,
                    values: [
                      { value: 'Home', odd: odds1x2.home },
                      ...(matchSport.primaryMarketHasDraw
                        ? [{ value: 'Draw', odd: odds1x2.draw }]
                        : []),
                      { value: 'Away', odd: odds1x2.away },
                    ],
                  }
                : null;

              const otherMarketsBuilt = buildMarketsForMatch(otherMarkets, frozenOrLiveMarkets) as MarketOdds[];
              const marketsForPicker: MarketOdds[] =
                matchWinnerMarket !== null ? [matchWinnerMarket, ...otherMarketsBuilt] : otherMarketsBuilt;

              if (marketsForPicker.length === 0) return null;

              return (
                <View key={match.id} style={styles.matchCard}>
                  {/* Match header — kits + stacked team names */}
                  <View style={styles.matchHeader}>
                    <View style={styles.sportIconWrap}>
                      <SportIcon sport={matchSport.key} size={20} />
                    </View>
                    {matchIsField ? (
                      <View style={styles.teamsColumn}>
                        <Text style={styles.teamName} numberOfLines={1}>{fieldEventName}</Text>
                        {fieldVenue !== '' && (
                          <Text style={styles.teamName} numberOfLines={1}>{fieldVenue}</Text>
                        )}
                      </View>
                    ) : (
                    <View style={styles.teamsColumn}>
                      <View style={styles.teamRow}>
                        {matchUsesKits
                          ? <SoccerKit primary={homeKit.primary} secondary={homeKit.secondary} type={homeKit.type} size={22} />
                          : <TeamCrest teamName={homeName} sport={matchSport.key} size={22} />}
                        <Text style={styles.teamName} numberOfLines={1}>{homeName}</Text>
                      </View>
                      <View style={styles.teamRow}>
                        {matchUsesKits
                          ? <SoccerKit primary={awayKit.primary} secondary={awayKit.secondary} type={awayKit.type} size={22} />
                          : <TeamCrest teamName={awayName} sport={matchSport.key} size={22} />}
                        <Text style={styles.teamName} numberOfLines={1}>{awayName}</Text>
                      </View>
                    </View>
                    )}
                    {(headerDate || headerTime) && (
                      <View style={styles.matchTimeBlock}>
                        {headerTime ? <Text style={styles.matchTime}>{headerTime}</Text> : null}
                        {headerDate ? <Text style={styles.matchDate}>{headerDate}</Text> : null}
                      </View>
                    )}
                  </View>

                  {/* Inline market picker */}
                  <View style={styles.marketArea}>
                    <InlineMarketPicker
                      gameId={match.id}
                      homeName={homeName}
                      awayName={awayName}
                      markets={marketsForPicker}
                      unlockedPremiumMarketIds={unlockedPremiumMarketIds}
                      isSelected={(g, marketId, value) => getDraftPick(g, marketId)?.value === value}
                      onPick={(g, marketId, marketName, value, odd) =>
                        setDraftPick(g, marketId, marketName, value, odd)
                      }
                    />
                  </View>
                </View>
              );
            })}

            {isFirstGameStarted && (
              <Text style={styles.startedMessage}>
                The first game has started. You can no longer accept or update picks for this challenge.
              </Text>
            )}
            {blockedByCoins && (
              <Text style={styles.startedMessage}>
                You need {challenge.stakeCoins ?? 0} coins to accept this challenge.
              </Text>
            )}

            <Pressable
              style={({ pressed, hovered }) => [
                styles.submitButton,
                isDisabled && styles.submitButtonDisabled,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && !isDisabled && styles.submitButtonPressed,
                hovered && Platform.OS === 'web' && !isDisabled && styles.submitButtonHover,
              ]}
              onPress={handleSubmit}
              disabled={isDisabled}
            >
              <Text style={styles.submitButtonText}>
                {submitting
                  ? 'Submitting...'
                  : isFirstGameStarted
                    ? 'Started'
                    : blockedByCoins
                      ? 'Not Enough Coins'
                      : 'Confirm & Accept'}
              </Text>
            </Pressable>
          </ScrollView>
        )}
      </SafeAreaView>
    </BaseModal>
  );
};
